(() => {
  if (customElements.get('featured-subscriptions')) return;

  /**
   * Feed amounts use major currency units:
   * "20.00" means €20.00, not 20 cents.
   *
   * Missing or invalid values must not become zero.
   */
  function parseAmount(value, fractionDigits) {
    if (typeof value !== 'string' && typeof value !== 'number') {
      return null;
    }

    const text = String(value).trim();

    if (!/^\d+(?:\.\d+)?$/.test(text)) {
      return null;
    }

    const amount = Number(text);
    const decimals = text.split('.')[1] || '';

    // Reject excess non-zero decimals instead of rounding a feed price.
    if (/[1-9]/.test(decimals.slice(fractionDigits))) {
      return null;
    }

    if (!Number.isFinite(amount) || amount < 0) {
      return null;
    }

    if (amount * 10 ** fractionDigits > Number.MAX_SAFE_INTEGER) {
      return null;
    }

    return amount;
  }

  function getOwnValue(object, key) {
    if (
      !object ||
      typeof object !== 'object' ||
      Array.isArray(object) ||
      !Object.prototype.hasOwnProperty.call(object, key)
    ) {
      return undefined;
    }

    return object[key];
  }

  class FeaturedSubscriptions extends HTMLElement {
    connectedCallback() {
      this.loadPrices();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    async loadPrices() {
      this.controller?.abort();

      const controller = new AbortController();
      this.controller = controller;

      const cards = Array.from(
        this.querySelectorAll('[data-subscription-key]')
      );

      const feedUrl = this.dataset.pricesFeedUrl?.trim();
      const currency = this.dataset.currency?.trim().toUpperCase();

      let timeout;

      cards.forEach((card) => {
        this.setState(card, 'unavailable');
      });

      if (!feedUrl || !cards.length) return;

      try {
        const url = new URL(feedUrl, window.location.origin);

        if (url.protocol !== 'https:') {
          throw new Error('Feed must use HTTPS.');
        }

        if (!/^[A-Z]{3}$/.test(currency || '')) {
          throw new Error('Missing storefront currency.');
        }

        const locale =
          this.dataset.locale ||
          document.documentElement.lang ||
          'en';

        const formatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency,
          currencyDisplay: 'symbol',
        });

        const fractionDigits =
          formatter.resolvedOptions().maximumFractionDigits;

        cards.forEach((card) => {
          this.setState(card, 'loading');
        });

        timeout = window.setTimeout(() => {
          controller.abort();
        }, 8000);

        const response = await fetch(url.href, {
          method: 'GET',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            Accept: 'application/json',
          },
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Feed returned HTTP ${response.status}.`);
        }

        const feed = await response.json();

        if (
          feed?.version !== 1 ||
          !feed.subscriptions ||
          typeof feed.subscriptions !== 'object' ||
          Array.isArray(feed.subscriptions)
        ) {
          throw new Error('Unsupported prices feed structure.');
        }

        // Ignore an old request if the section has been reloaded or removed.
        if (this.controller !== controller || !this.isConnected) {
          return;
        }

        cards.forEach((card) => {
          const key = card.dataset.subscriptionKey.trim();

          const subscription = getOwnValue(
            feed.subscriptions,
            key
          );

          const prices = getOwnValue(subscription, currency);

          let displayed = 0;

          card.querySelectorAll('[data-price-line]').forEach((line) => {
            const rawAmount = getOwnValue(
              prices,
              line.dataset.priceLine
            );

            const amount = parseAmount(rawAmount, fractionDigits);
            const amountElement = line.querySelector('[data-price-amount]');

            line.hidden = amount === null;

            amountElement.textContent =
              amount === null ? '' : formatter.format(amount);

            if (amount !== null) {
              displayed += 1;
            }
          });

          this.setState(
            card,
            displayed ? 'ready' : 'unavailable'
          );
        });
      } catch (error) {
        if (this.controller !== controller || !this.isConnected) {
          return;
        }

        cards.forEach((card) => {
          this.setState(card, 'unavailable');
        });

        if (window.Shopify?.designMode) {
          console.warn(
            '[Featured subscriptions] Prices unavailable:',
            error.message
          );
        }
      } finally {
        window.clearTimeout(timeout);
      }
    }

    setState(card, state) {
      const container = card.querySelector('[data-subscription-prices]');
      const status = card.querySelector('[data-price-status]');

      // Keep the original, translatable Liquid fallback on reconnection.
      if (!status.hasAttribute('data-fallback-text')) {
        status.dataset.fallbackText = status.textContent.trim();
      }

      container.dataset.state = state;

      container.setAttribute(
        'aria-busy',
        state === 'loading' ? 'true' : 'false'
      );

      status.hidden = state === 'ready';

      status.textContent =
        state === 'loading'
          ? this.dataset.loadingText || status.dataset.fallbackText
          : status.dataset.fallbackText;

      if (state !== 'ready') {
        card.querySelectorAll('[data-price-line]').forEach((line) => {
          line.hidden = true;
        });
      }
    }
  }

  customElements.define(
    'featured-subscriptions',
    FeaturedSubscriptions
  );
})();